import React, { useEffect } from 'react';
import useGameStore from '../engine/store';
import { getSeverityColor, getSeverityBg } from '../engine/economyEngine';

const severityLabels = {
  positive: { icon: '📈', label: 'GOOD NEWS', btnText: '👍 Noted', btnClass: 'bg-neon-green/20 border-neon-green/40 text-neon-green hover:bg-neon-green/30' },
  negative: { icon: '📉', label: 'WARNING', btnText: '💪 Adapt', btnClass: 'bg-neon-orange/20 border-neon-orange/40 text-neon-orange hover:bg-neon-orange/30' },
  neutral: { icon: 'ℹ️', label: 'UPDATE', btnText: '👍 Got It', btnClass: 'bg-neon-blue/20 border-neon-blue/40 text-neon-blue hover:bg-neon-blue/30' },
  catastrophic: { icon: '🚨', label: 'CRISIS', btnText: '😰 Brace', btnClass: 'bg-red-500/20 border-red-500/40 text-red-400 hover:bg-red-500/30' },
  windfall: { icon: '🎉', label: 'OPPORTUNITY', btnText: '🎯 Seize It', btnClass: 'bg-neon-yellow/20 border-neon-yellow/40 text-neon-yellow hover:bg-neon-yellow/30' },
};

export default function EventModal() {
  const { showEventModal, currentEventModal, dismissEvent } = useGameStore();

  useEffect(() => {
    if (!showEventModal) return;
    const handler = (e) => {
      if (e.code === 'Enter' || e.code === 'Escape') {
        e.preventDefault();
        dismissEvent();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showEventModal, dismissEvent]);

  if (!showEventModal || !currentEventModal) return null;

  const event = currentEventModal;
  const color = getSeverityColor(event.severity);
  const config = severityLabels[event.severity] || severityLabels.neutral;

  // Describe effects for the player
  const effectTexts = [];
  const e = event.effects;
  if (e.defaultMod && e.defaultMod !== 1) {
    effectTexts.push(e.defaultMod > 1
      ? `Default rates +${((e.defaultMod - 1) * 100).toFixed(0)}%`
      : `Default rates ${((e.defaultMod - 1) * 100).toFixed(0)}%`
    );
  }
  if (e.demandMod && e.demandMod !== 1) {
    effectTexts.push(e.demandMod > 1
      ? `Loan demand +${((e.demandMod - 1) * 100).toFixed(0)}%`
      : `Loan demand ${((e.demandMod - 1) * 100).toFixed(0)}%`
    );
  }
  if (e.rateMod) effectTexts.push(`Market rates ${e.rateMod > 0 ? '+' : ''}${(e.rateMod * 100).toFixed(1)}%`);
  if (e.capitalHit) effectTexts.push(`Capital hit: -${(e.capitalHit * 100).toFixed(0)}%`);
  if (e.capitalBoost) effectTexts.push(`Capital boost: +${(e.capitalBoost * 100).toFixed(0)}%`);
  if (e.sectorDefaultMod) {
    for (const [sector, mod] of Object.entries(e.sectorDefaultMod)) {
      const label = sector.charAt(0).toUpperCase() + sector.slice(1);
      effectTexts.push(mod > 1
        ? `${label} defaults +${((mod - 1) * 100).toFixed(0)}%`
        : `${label} defaults ${((mod - 1) * 100).toFixed(0)}%`
      );
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={dismissEvent} />

      <div className={`relative max-w-md w-full rounded-2xl border-2 bg-dark-800 animate-slide-up shadow-2xl overflow-hidden`}
        style={{ borderColor: `${color}50`, boxShadow: `0 0 60px ${color}15` }}>
        <div className="h-1.5 w-full" style={{ background: color }} />

        <div className="p-6">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-black uppercase tracking-widest" style={{ color }}>{config.icon} {config.label}</span>
          </div>
          <h2 className="text-2xl font-black text-white mb-2">{event.title}</h2>
          <p className="text-gray-300 text-sm leading-relaxed mb-4">{event.description}</p>

          {effectTexts.length > 0 && (
            <div className="glass-panel p-3 mb-4">
              <div className="text-xs text-gray-500 uppercase mb-2">Effects</div>
              <div className="space-y-1">
                {effectTexts.map((text, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: color }} />
                    <span className="text-gray-300">{text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
            <span>Duration: {event.duration} months</span>
            <span className="text-gray-600">Press Enter</span>
          </div>

          <button onClick={dismissEvent}
            className={`w-full py-3 rounded-xl font-bold text-sm border transition-all active:scale-95 ${config.btnClass}`}>
            {config.btnText}
          </button>
        </div>
      </div>
    </div>
  );
}
